import React from 'react'
import { Button, ButtonPassThroughOptions } from 'primereact/button'

export interface MkAccountMenuWidgetProps extends React.HTMLAttributes<HTMLUListElement> {
  className?: string
  buttonConfigs?: {
    key: string
    id?: number | string
    itemClassName: string
    label: string
    onClick: (e: any) => void
    icon?: React.ReactNode
    ptConfig: Partial<ButtonPassThroughOptions>
    children?: React.ReactNode
  }[]
}

/**
 * MkAccountMenuWidget component renders a list of buttons based on the provided configuration.
 *
 * @param {MkAccountMenuWidgetProps} props - The properties for the MkAccountMenuWidget component.
 * @param {string} props.className - The CSS class name to apply to the outer <ul> element.
 * @param {Array} props.buttonConfigs - An array of button configuration objects.
 *
 * @returns {JSX.Element} The rendered MkAccountMenuWidget component.
 *
 * @component
 * @example
 * const buttonConfigs = [
 *   {
 *     key: 'profile',
 *     label: 'Profile',
 *     onClick: handleProfileClick,
 *     ptConfig: { /* pass-through options * / },
 *     icon: <ProfileIcon />,
 *     children: null,
 *     itemClassName: 'profile-item'
 *   },
 *   {
 *     key: 'settings',
 *     label: 'Settings',
 *     onClick: handleSettingsClick,
 *     ptConfig: { /* pass-through options * / },
 *     icon: <SettingsIcon />,
 *     children: null,
 *     itemClassName: 'settings-item'
 *   }
 * ];
 *
 * <MkAccountMenuWidget className="account-menu" buttonConfigs={buttonConfigs} />
 */
export const MkAccountMenuWidget: React.FC<MkAccountMenuWidgetProps> = ({ className, buttonConfigs }) => {
  const renderAccountMenuButton = React.useCallback(
    (idx: number, label: string, onClick: (e: any) => void, ptConfig: Partial<ButtonPassThroughOptions>, icon?: React.ReactNode, children?: React.ReactNode, config?: any) => {
      let dataTestId = config['data-testid'];

      if (config.key === 'account-widget-signIn') {
        dataTestId = 'mk-signin-button';
      } else if (config.key === 'account-widget-avatar') {
        dataTestId = 'mk-account-button';
      }

      return (
        <Button
          key={idx + label}
          icon={icon}
          onClick={onClick}
          pt={ptConfig}
          {...(label && !children ? { label } : {})}
          {...(dataTestId ? { 'data-testid': dataTestId } : {})}
        >
          {children}
        </Button>
      );
    },
    [buttonConfigs],
  )

  return (
    <ul className={className}>
      {buttonConfigs?.map((config, idx) => (
        <li key={config?.key} className={`${config.itemClassName} ${config.id}`}>
          {renderAccountMenuButton(idx, config.label, config.onClick, config.ptConfig, config.icon, config.children, config)}
        </li>
      ))}
    </ul>
  )
}
